/**
 * Step 3 — Rendering Provider Search, Network Validation & Acceptance Confirmation
 * 
 * Three search modes: Individual / Facility / AI-NL
 * Per-result network status + NPPES validation
 * Admin confirms fax + email → triggers mock eFax + mock secure email
 * Status timeline with reminder send + accept/decline tracking
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Sparkles, Building2, User as UserIcon, Loader2, Check, X,
  ShieldCheck, ShieldAlert, ShieldX, Phone, Mail, MapPin, Send,
  Clock, AlertTriangle, Copy, RefreshCw, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Step3RenderingProvider({
  t, isDark, paId, payer = "BCBSTX", planType = "PPO",
  selectedProvider, initialConfirmation = null, onProviderSelected, onConfirmationSent,
}) {
  // Search state
  const [mode, setMode] = useState("individual");
  const [indFirst, setIndFirst] = useState("");
  const [indLast, setIndLast] = useState("");
  const [indNpi, setIndNpi] = useState("");
  const [indSpec, setIndSpec] = useState("");
  const [indCity, setIndCity] = useState("");
  const [facName, setFacName] = useState("");
  const [facNpi, setFacNpi] = useState("");
  const [facCity, setFacCity] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiParsed, setAiParsed] = useState(null);

  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [nppesInfo, setNppesInfo] = useState({}); // npi -> result

  // Confirm-contact dialog
  const [confirmFax, setConfirmFax] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(initialConfirmation);

  // Sync confirmation when parent re-loads a saved PA
  useEffect(() => {
    if (initialConfirmation) setConfirmation(initialConfirmation);
  }, [initialConfirmation]);

  // Plan rules
  const [planRules, setPlanRules] = useState({ oon_action: "warn", label: "" });

  useEffect(() => {
    axios.get(`${API}/rendering-provider/plan-rules`, {
      params: { plan_type: planType }, withCredentials: true,
    }).then(r => setPlanRules(r.data)).catch(() => {});
  }, [planType]);

  const doSearch = async () => {
    setSearching(true); setResults([]);
    try {
      if (mode === "individual") {
        const r = await axios.get(`${API}/rendering-provider/search/individual`, {
          params: { payer, first_name: indFirst, last_name: indLast, npi: indNpi, specialty: indSpec, city: indCity },
          withCredentials: true,
        });
        setResults(r.data.providers || []);
      } else if (mode === "facility") {
        const r = await axios.get(`${API}/rendering-provider/search/facility`, {
          params: { payer, name: facName, npi: facNpi, city: facCity },
          withCredentials: true,
        });
        setResults(r.data.providers || []);
      } else {
        const r = await axios.post(`${API}/rendering-provider/search/ai`,
          { query: aiQuery, payer }, { withCredentials: true });
        setAiParsed(r.data.parsed_filters);
        setResults(r.data.providers || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const validateNppes = async (npi) => {
    try {
      const r = await axios.get(`${API}/rendering-provider/validate-nppes/${npi}`, { withCredentials: true });
      setNppesInfo(prev => ({ ...prev, [npi]: r.data }));
    } catch (e) {
      setNppesInfo(prev => ({ ...prev, [npi]: { active: null, status: "API_ERROR" } }));
    }
  };

  const selectProvider = (p) => {
    // Hard-stop check for HMO/EPO/Medicaid + out-of-network
    if (!p.in_network && planRules.oon_action === "block") {
      toast.error(`${planType} plans do not allow out-of-network providers — please choose another.`);
      return;
    }
    setConfirmFax(p.fax || "");
    setConfirmEmail(p.email || "");
    onProviderSelected(p);
  };

  const sendConfirmation = async () => {
    if (!selectedProvider) return;
    if (!confirmFax && !confirmEmail) {
      toast.error("Provide at least one of fax or email");
      return;
    }
    if (!paId) {
      toast.error("Save the PA request first before sending confirmation");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/rendering-provider/${paId}/confirm-contact`, {
        npi: selectedProvider.npi,
        fax: confirmFax,
        email: confirmEmail,
        provider: selectedProvider,
      }, { withCredentials: true });
      setConfirmation(r.data);
      onConfirmationSent && onConfirmationSent(r.data);
      toast.success("Mock eFax + secure email sent to provider");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send confirmation");
    } finally {
      setSending(false);
    }
  };

  const sendReminder = async () => {
    if (!paId) return;
    try {
      const r = await axios.post(`${API}/rendering-provider/${paId}/send-reminder`, {}, { withCredentials: true });
      if (r.data.ok) toast.success(`Reminder #${r.data.reminder_count} sent`);
      else toast.info(r.data.reason);
    } catch (e) {
      toast.error("Reminder failed");
    }
  };

  const surfaceCard = `${t.card} rounded-lg border shadow-sm`;

  return (
    <div className="space-y-6" data-testid="step-3-rendering-provider">
      {/* Header card with payer context */}
      <div className={`${surfaceCard} p-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isDark ? "bg-emerald-500/10" : "bg-blue-50"}`}>
            <ShieldCheck className={`w-5 h-5 ${isDark ? "text-emerald-400" : "text-blue-600"}`} />
          </div>
          <div>
            <div className={`text-sm font-semibold ${t.textStrong}`}>Search Rendering / Referred-To Provider</div>
            <div className={`text-xs ${t.textMuted}`}>
              Searching <span className="font-semibold">{payer}</span> · {planType} network · OON: <span className="font-semibold">{planRules.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search tabs */}
      <div className={surfaceCard}>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className={`grid grid-cols-3 w-full rounded-none rounded-t-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
            <TabsTrigger value="individual" data-testid="tab-individual"><UserIcon className="w-4 h-4 mr-1.5" />Individual</TabsTrigger>
            <TabsTrigger value="facility" data-testid="tab-facility"><Building2 className="w-4 h-4 mr-1.5" />Facility</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles className="w-4 h-4 mr-1.5" />Smart Search</TabsTrigger>
          </TabsList>

          <TabsContent value="individual" className="p-5 space-y-3 m-0">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={`text-xs ${t.label}`}>First Name</Label>
                <Input value={indFirst} onChange={e => setIndFirst(e.target.value)} className={`mt-1 ${t.input}`} data-testid="ind-first" /></div>
              <div><Label className={`text-xs ${t.label}`}>Last Name</Label>
                <Input value={indLast} onChange={e => setIndLast(e.target.value)} className={`mt-1 ${t.input}`} data-testid="ind-last" /></div>
              <div><Label className={`text-xs ${t.label}`}>NPI</Label>
                <Input value={indNpi} onChange={e => setIndNpi(e.target.value)} placeholder="10-digit NPI" className={`mt-1 ${t.input}`} data-testid="ind-npi" /></div>
              <div><Label className={`text-xs ${t.label}`}>Specialty</Label>
                <Input value={indSpec} onChange={e => setIndSpec(e.target.value)} placeholder="cardio, ortho, neuro..." className={`mt-1 ${t.input}`} data-testid="ind-spec" /></div>
              <div><Label className={`text-xs ${t.label}`}>City</Label>
                <Input value={indCity} onChange={e => setIndCity(e.target.value)} placeholder="Plano, Frisco..." className={`mt-1 ${t.input}`} data-testid="ind-city" /></div>
            </div>
            <Button onClick={doSearch} disabled={searching} className={t.btn} data-testid="search-ind-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Search Individual Providers
            </Button>
          </TabsContent>

          <TabsContent value="facility" className="p-5 space-y-3 m-0">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={`text-xs ${t.label}`}>Facility Name</Label>
                <Input value={facName} onChange={e => setFacName(e.target.value)} placeholder="Imaging center, hospital..." className={`mt-1 ${t.input}`} data-testid="fac-name" /></div>
              <div><Label className={`text-xs ${t.label}`}>Facility NPI</Label>
                <Input value={facNpi} onChange={e => setFacNpi(e.target.value)} className={`mt-1 ${t.input}`} data-testid="fac-npi" /></div>
              <div><Label className={`text-xs ${t.label}`}>City</Label>
                <Input value={facCity} onChange={e => setFacCity(e.target.value)} className={`mt-1 ${t.input}`} data-testid="fac-city" /></div>
            </div>
            <Button onClick={doSearch} disabled={searching} className={t.btn} data-testid="search-fac-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Search Facilities
            </Button>
          </TabsContent>

          <TabsContent value="ai" className="p-5 space-y-3 m-0">
            <div>
              <Label className={`text-xs ${t.label}`}>Ask in your own words</Label>
              <Textarea
                value={aiQuery} onChange={e => setAiQuery(e.target.value)}
                placeholder='e.g., "Find in-network cardiologist near Plano accepting new patients"'
                rows={2} className={`mt-1 ${t.input}`} data-testid="ai-query"
              />
              <p className={`text-[11px] ${t.textMuted} mt-1`}>
                Gemini parses specialty, city, plan, and network filters from your query.
              </p>
            </div>
            <Button onClick={doSearch} disabled={searching || !aiQuery} className={t.btn} data-testid="search-ai-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Search with AI
            </Button>
            {aiParsed && (
              <div className={`text-xs p-2 rounded ${isDark ? "bg-slate-800 text-slate-400" : "bg-gray-50 text-gray-600"}`}>
                <span className="font-semibold">Parsed:</span> specialty=<span className="font-mono">{aiParsed.specialty || "—"}</span> · city=<span className="font-mono">{aiParsed.city || "—"}</span> · in-network={String(aiParsed.in_network_only)} · accepting={String(aiParsed.accepting_only)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className={surfaceCard}>
          <div className={`px-5 py-3 border-b ${t.cardHead} flex items-center justify-between`}>
            <h3 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Results · {results.length}</h3>
            <Badge variant="outline">{payer} · {planType}</Badge>
          </div>
          <div className="divide-y" style={{ borderColor: isDark ? "rgb(30,41,59)" : "rgb(229,231,235)" }}>
            {results.map((p, i) => {
              const nppes = nppesInfo[p.npi];
              const isSelected = selectedProvider?.npi === p.npi;
              const oonBlocked = !p.in_network && planRules.oon_action === "block";

              return (
                <div key={p.npi + i} className={`p-4 ${isSelected ? (isDark ? "bg-emerald-500/5" : "bg-blue-50/60") : ""}`}
                  data-testid={`provider-result-${p.npi}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                      {p.is_facility ? <Building2 className="w-5 h-5" /> : <UserIcon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={`text-sm font-bold ${t.textStrong}`}>{p.display_name}</h4>
                        <span className={`text-xs font-mono ${t.textMuted}`}>NPI {p.npi}</span>
                        {p.in_network
                          ? <Badge className="bg-emerald-500 text-white text-[10px] flex items-center gap-1"><ShieldCheck className="w-3 h-3" />In-Network</Badge>
                          : <Badge className="bg-red-500 text-white text-[10px] flex items-center gap-1"><ShieldX className="w-3 h-3" />Out-of-Network</Badge>}
                        {p.accepting_new_patients
                          ? <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Accepting New Patients</Badge>
                          : <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Not Accepting</Badge>}
                      </div>
                      <div className={`text-xs ${t.textMuted} mt-1`}>{p.specialty} · Taxonomy {p.taxonomy_code}</div>
                      {p.organization && <div className={`text-xs ${t.text} mt-0.5`}>{p.organization}</div>}
                      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-xs mt-2 ${t.textMuted}`}>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.address}, {p.city} {p.state} {p.zip}</span>
                        {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                        {p.fax && <span className="flex items-center gap-1">Fax: {p.fax}</span>}
                        {p.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px]">{p.network_name || "—"}</Badge>
                        <Badge variant="outline" className="text-[10px]">Confidence {(p.confidence_score * 100).toFixed(0)}%</Badge>
                        <Badge variant="outline" className="text-[10px]">{p.verification_source}</Badge>
                        {nppes ? (
                          nppes.active === true
                            ? <Badge className="bg-emerald-600 text-white text-[10px]"><Check className="w-3 h-3 mr-1" />NPPES Active</Badge>
                            : nppes.active === false
                              ? <Badge className="bg-red-600 text-white text-[10px]">NPPES {nppes.status}</Badge>
                              : <Badge className="bg-amber-500 text-white text-[10px]">NPPES Error</Badge>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => validateNppes(p.npi)} data-testid={`nppes-btn-${p.npi}`}>
                            <RefreshCw className="w-3 h-3 mr-1" />Verify with NPPES
                          </Button>
                        )}
                      </div>
                      {!p.in_network && planRules.oon_action === "warn" && !isSelected && (
                        <div className={`mt-2 p-2 rounded text-xs flex items-start gap-2 ${isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-800"}`}>
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>{planType} allows out-of-network with higher cost share. Patient may face balance billing.</span>
                        </div>
                      )}
                      {oonBlocked && (
                        <div className={`mt-2 p-2 rounded text-xs flex items-start gap-2 ${isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-800"}`}>
                          <ShieldX className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>{planType} plans do not cover out-of-network — cannot proceed with this provider.</span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => selectProvider(p)}
                      disabled={oonBlocked}
                      variant={isSelected ? "default" : "outline"}
                      className={isSelected ? t.btn : t.btnOutline}
                      data-testid={`select-prov-${p.npi}`}
                    >
                      {isSelected ? <><Check className="w-4 h-4 mr-1" />Selected</> : "Select"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm Contact Card */}
      {selectedProvider && !confirmation && (
        <div className={surfaceCard}>
          <div className={`px-5 py-3 border-b ${t.cardHead}`}>
            <h3 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Confirm Contact Details</h3>
            <p className={`text-xs ${t.textMuted} mt-1`}>
              Confirm or update the fax + email below. eFax goes to the rendering provider;
              the secure email contains a unique link + 6-digit code for them to accept the patient.
            </p>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <div>
              <Label className={`text-xs ${t.label}`}>Fax Number</Label>
              <Input value={confirmFax} onChange={e => setConfirmFax(e.target.value)} className={`mt-1 ${t.input}`} data-testid="confirm-fax" />
            </div>
            <div>
              <Label className={`text-xs ${t.label}`}>Email Address</Label>
              <Input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} className={`mt-1 ${t.input}`} data-testid="confirm-email" />
            </div>
            <div className="col-span-2">
              <Button onClick={sendConfirmation} disabled={sending || (!confirmFax && !confirmEmail)} className={t.btn} data-testid="send-confirmation-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send eFax & Secure Email
              </Button>
              <p className={`text-[11px] ${t.textMuted} mt-2`}>
                <span className="font-semibold">MOCKED:</span> eFax + email are simulated for sandbox. The secure link is shown after sending.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation result + timeline */}
      {confirmation && (
        <div className={surfaceCard}>
          <div className={`px-5 py-3 border-b ${t.cardHead} flex items-center justify-between`}>
            <h3 className={`text-sm font-bold ${t.cardHeadText} uppercase flex items-center gap-2`}>
              <Clock className="w-4 h-4" />Provider Confirmation Status
            </h3>
            <Button size="sm" variant="outline" onClick={sendReminder} className={t.btnOutline} data-testid="reminder-btn">
              <Bell className="w-3 h-3 mr-1" />Send Reminder
            </Button>
          </div>
          <div className="p-5 space-y-4">
            {/* Inline status pills — quick scan for demo */}
            <div className="flex flex-wrap items-center gap-2" data-testid="confirmation-status-row">
              {confirmation.efax && (
                <Badge className="bg-emerald-500 text-white text-xs flex items-center gap-1" data-testid="status-efax-sent">
                  <Check className="w-3 h-3" /> eFax Sent · {confirmation.efax.delivery_id}
                </Badge>
              )}
              {confirmation.email && (
                <Badge className="bg-blue-500 text-white text-xs flex items-center gap-1" data-testid="status-email-sent">
                  <Mail className="w-3 h-3" /> Secure Email Sent
                </Badge>
              )}
              <Badge variant="outline" className="text-xs flex items-center gap-1 border-amber-400 text-amber-700" data-testid="status-accepting-pending">
                <Clock className="w-3 h-3" /> Accepting New Patient: Awaiting Practice Confirmation
              </Badge>
              {selectedProvider?.accepting_new_patients !== undefined && (
                <Badge variant="outline" className={`text-xs ${selectedProvider.accepting_new_patients ? "border-emerald-400 text-emerald-700" : "border-red-400 text-red-700"}`} data-testid="status-directory-accepting">
                  Directory says: {selectedProvider.accepting_new_patients ? "Accepting" : "Not Accepting"}
                </Badge>
              )}
            </div>

            {/* Practice Confirmation Link — prominent demo-shareable banner */}
            <div className={`p-4 rounded-lg border-2 ${isDark ? "bg-indigo-500/10 border-indigo-500/40" : "bg-indigo-50 border-indigo-300"}`} data-testid="practice-confirmation-link-card">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${isDark ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
                  <Send className={`w-4 h-4 ${isDark ? "text-indigo-300" : "text-indigo-700"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-bold ${t.textStrong}`}>Practice Confirmation Link</div>
                  <p className={`text-xs ${t.textMuted} mt-0.5 mb-2`}>
                    Share this with the rendering practice. They open it to confirm whether the provider accepts the new patient. Verification code: <span className={`font-mono font-bold ${t.textStrong}`}>{confirmation.secondary_code}</span>
                  </p>
                  <div className={`flex items-center gap-2 p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"} border ${isDark ? "border-slate-700" : "border-gray-300"}`}>
                    <code className={`flex-1 text-xs font-mono break-all ${isDark ? "text-emerald-300" : "text-indigo-700"}`} data-testid="practice-link-url">
                      {window.location.origin + confirmation.secure_link}
                    </code>
                    <Button size="sm" variant="ghost" onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + confirmation.secure_link);
                      toast.success("Practice confirmation link copied to clipboard");
                    }} className="h-7 px-2 text-xs" data-testid="copy-practice-link-btn">
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                    <Button size="sm" onClick={() => window.open(confirmation.secure_link, "_blank")} className={`h-7 px-2 text-xs ${t.btn}`} data-testid="open-practice-link-btn">
                      Open
                    </Button>
                  </div>
                  <p className={`text-[11px] ${t.textMuted} mt-2`}>
                    Link expires <span className="font-semibold">{new Date(confirmation.expires_at).toLocaleString()}</span>. The practice must enter the verification code to respond.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {confirmation.efax && (
                <div className={`p-3 rounded border-l-4 border-l-emerald-500 ${isDark ? "bg-slate-800/40" : "bg-emerald-50/60"} border`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className={`text-sm font-semibold ${t.textStrong}`}>eFax Sent</span>
                  </div>
                  <div className={`text-xs ${t.textMuted}`}>
                    To: {confirmation.efax.fax_to}<br />
                    Delivery ID: <span className="font-mono">{confirmation.efax.delivery_id}</span><br />
                    Sent: {new Date(confirmation.efax.sent_at).toLocaleString()}
                  </div>
                </div>
              )}
              {confirmation.email && (
                <div className={`p-3 rounded border-l-4 border-l-blue-500 ${isDark ? "bg-slate-800/40" : "bg-blue-50/60"} border`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span className={`text-sm font-semibold ${t.textStrong}`}>Secure Email Sent</span>
                  </div>
                  <div className={`text-xs ${t.textMuted} space-y-1`}>
                    <div>To: {confirmation.email.email_to}</div>
                    <div>Expires: {new Date(confirmation.email.expires_at).toLocaleString()}</div>
                  </div>
                </div>
              )}
            </div>
            <div className={`p-3 rounded text-xs ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-800"} flex items-start gap-2`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Status: <span className="font-semibold">Awaiting Rendering Provider Response</span>. Open the practice link above to simulate the practice confirming.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
